import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { ReactElement, ReactNode, useState } from 'react';

import { AutoCompleteOption } from '../types/AutoCompleteOption';

export interface AutoCompleteProps<T> {
  /** Array of wrapped options (value + metadata) */
  data: Array<AutoCompleteOption<T>>;

  /** Currently selected value (unwrapped) */
  value: T | null;

  /** Called when selection changes (unwrapped value) */
  onChange: (value: T | null) => void;

  /** Function to display the selected value in the input */
  displayValue: (value: T | null) => string;

  /** Function to render an option in the dropdown */
  renderOption: (value: T) => ReactNode;

  /** Label text for the input */
  labelText: string;

  /** Placeholder text */
  placeholder?: string;

  /** Optional icon to display in the input */
  Icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;

  /** Function to generate unique key for each option */
  optionKeyMethod?: (value: T) => string;

  /** Text to show when no options match */
  notFoundText?: string;

  /** Show loading state */
  loading?: boolean;

  /** Disable the entire component */
  disabled?: boolean;

  /** Has error state (for styling) */
  hasError?: boolean;

  /**
   * Optional footer content rendered below options
   * When provided, dropdown will show even with no options
   */
  renderFooter?: () => ReactNode;

  /** Callback when query text changes */
  onQueryChange?: (query: string) => void;
}

export const AutoComplete = <T extends { id: string }>({
  data = [],
  value,
  disabled = false,
  onChange,
  displayValue,
  renderOption,
  labelText,
  placeholder,
  Icon,
  optionKeyMethod,
  notFoundText = 'No results found',
  loading = false,
  hasError = false,
  renderFooter,
  onQueryChange,
}: AutoCompleteProps<T>): ReactElement => {
  const [hasInput, setHasInput] = useState(false);

  // Find the wrapped option for the current value
  // If not found in data, create a wrapped option for it anyway
  // This allows selected values to display in input even when filtered from dropdown
  const selectedOption = value
    ? (data.find((opt) => opt.value.id === value.id) ?? { value, metadata: {} })
    : null;

  const hasOptions = data.length > 0;
  const showFooter = !!renderFooter;
  const showDropdown = hasOptions || showFooter || hasInput;

  // HeadlessUI v2 needs a comparator to handle object value comparison
  // when the data array is recreated on each render
  const compareOptions = (
    a: AutoCompleteOption<T> | null,
    b: AutoCompleteOption<T> | null,
  ) => {
    if (a === null || b === null) return a === b;
    return a.value.id === b.value.id;
  };

  return (
    <Combobox
      as="div"
      className="w-full"
      value={selectedOption}
      disabled={disabled}
      onChange={(option: AutoCompleteOption<T> | null) => {
        // Unwrap the value before calling onChange
        onChange(option?.value ?? null);
      }}
      by={compareOptions}
    >
      <Label
        className={classNames('text-sm font-medium text-gray-700', {
          'sr-only': !labelText,
        })}
      >
        {labelText}
      </Label>
      <div className="relative mt-1">
        {Icon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Icon className="size-5 text-gray-400" aria-hidden="true" />
          </div>
        )}
        <ComboboxInput
          autoComplete="off"
          placeholder={placeholder}
          className={classNames(
            'w-full rounded-md border py-2 pr-10 text-sm shadow-sm',
            {
              'pl-10': Icon,
              'bg-white': !disabled,
              'bg-gray-100 cursor-not-allowed': disabled,
              'border-gray-300': !hasError,
              'border-red-500': hasError,
            },
          )}
          onChange={(event) => {
            const newQuery = event.target.value;

            // Track if user has typed anything
            setHasInput(newQuery.length > 0);

            // Clear value when input is cleared
            if (newQuery === '' && value) {
              onChange(null);
            }

            // Notify parent of query change
            onQueryChange?.(newQuery);
          }}
          displayValue={(option: AutoCompleteOption<T> | null) => {
            if (!option) return '';
            return displayValue(option.value);
          }}
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon
            className="size-5 text-gray-400"
            aria-hidden="true"
          />
        </ComboboxButton>

        {showDropdown && (
          <ComboboxOptions className="absolute z-10 mt-1 flex max-h-80 w-full flex-col overflow-hidden rounded-md border border-gray-300 bg-white shadow-lg">
            {/* Scrollable options area */}
            {hasOptions && (
              <div className="overflow-auto py-1 text-sm">
                {data.map((option) => {
                  const key = optionKeyMethod
                    ? optionKeyMethod(option.value)
                    : option.value.id;
                  const isDisabled = option.metadata?.disabled ?? false;
                  const metadata = option.metadata?.label;

                  return (
                    <ComboboxOption
                      key={key}
                      value={option}
                      disabled={isDisabled}
                      className={({ active, selected }) =>
                        classNames(
                          'relative cursor-default select-none py-2.5 pl-3 pr-10 transition-colors',
                          {
                            // Hover state - subtle left border accent
                            'border-l-2 border-l-primaryBlue/60 bg-blue-50/70':
                              active && !selected && !isDisabled,
                            'border-l-2 border-l-transparent':
                              !active || selected || isDisabled,
                            // Selected state
                            'bg-primaryBlue/10 text-gray-900':
                              selected && !isDisabled,
                            // Default state
                            'text-gray-700':
                              !active && !selected && !isDisabled,
                            // Disabled state
                            'cursor-not-allowed bg-gray-50': isDisabled,
                          },
                        )
                      }
                    >
                      {({ selected }) => (
                        <div className="flex items-center justify-between">
                          <div
                            className={classNames({
                              'text-gray-400 line-through': isDisabled,
                              'font-medium': selected,
                            })}
                          >
                            {renderOption(option.value)}
                          </div>
                          <div className="flex items-center gap-2">
                            {metadata && (
                              <span
                                className={classNames(
                                  'rounded-full px-2 py-0.5 text-xs',
                                  {
                                    'bg-amber-100 text-amber-700': isDisabled,
                                    'bg-gray-100 text-gray-500': !isDisabled,
                                  },
                                )}
                              >
                                {metadata}
                              </span>
                            )}
                            {selected && !isDisabled && (
                              <CheckIcon className="text-primaryBlue size-4" />
                            )}
                          </div>
                        </div>
                      )}
                    </ComboboxOption>
                  );
                })}
              </div>
            )}

            {/* Show loading or no results message */}
            {!hasOptions && !showFooter && hasInput && (
              <div className="px-3 py-2 text-center text-sm text-gray-500">
                {loading ? 'Loading...' : notFoundText}
              </div>
            )}

            {/* Fixed footer */}
            {showFooter && (
              <div className="border-t border-gray-200 bg-white">
                {renderFooter()}
              </div>
            )}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
};
